import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
} from "@mui/material";
import { Assignment, Upload, Delete, Error, Info, CheckCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
// TODO(api): Replace direct captationService import with API call
// import { captationService, // TODO(shared): Replace PROPERTY_TYPES with shared constant
// PROPERTY_TYPES } from '../services/captationService';
import { opportunitiesService } from "../opportunities/opportunitiesService";

// Fallback definition for PROPERTY_TYPES
const PROPERTY_TYPES = {
    RESIDENTIAL: "residential",
    COMMERCIAL: "commercial",
    INDUSTRIAL: "industrial",
    LAND: "land",
    MIXED_USE: "mixed_use",
    HOSPITALITY: "hospitality",
    OFFICE: "office",
    RETAIL: "retail",
    WAREHOUSE: "warehouse",
    DEVELOPMENT: "development",
};

const steps = ["Información Básica", "Detalles del Proyecto", "Documentación", "Revisión"];

const OptimizedAgentOpportunityForm = ({ open, onClose, onSuccess, project }) => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [validation, setValidation] = useState(null);

    const [form, setForm] = useState({
        title: "",
        description: "",
        propertyType: "",
        location: "",
        totalBudget: "",
        expectedReturn: "",
        executionTime: "",
        minimumInvestment: "",
        riskLevel: "medium",
        media: [],
        profitabilityAnalysis: null,
        notaSimple: null,
        projectBrochure: null,
        additionalDocuments: [],
    });

    useEffect(() => {
        if (project) {
            setForm({
                title: project.title || "",
                description: project.description || "",
                propertyType: project.propertyType || "",
                location: project.location || "",
                totalBudget: project.totalBudget || "",
                expectedReturn: project.expectedReturn || "",
                executionTime: project.executionTime || "",
                minimumInvestment: project.minimumInvestment || "",
                riskLevel: project.riskLevel || "medium",
                media: project.media || [],
                profitabilityAnalysis: project.profitabilityAnalysis || null,
                notaSimple: project.notaSimple || null,
                projectBrochure: project.projectBrochure || null,
                additionalDocuments: project.additionalDocuments || [],
            });
        }
        setActiveStep(0);
        setValidation(null);
    }, [project, open]);

    const validateForm = useCallback(async (_formData) => {
        try {
            // TODO(api): Replace captationService usage with API call
            // const result = await captationService.validateProjectData(formData);
            const result = { 
                isValid: true, 
                score: 85,
                issues: [], 
                recommendations: ["Considera agregar más imágenes del proyecto", "La documentación está completa"]
            }; // Temporary mock result
            setValidation(result);
            return result;
        } catch (error) {
            return null;
        }
    }, []);

    useEffect(() => {
        if (form.title || form.description || form.totalBudget) {
            const timer = setTimeout(() => validateForm(form), 1000);
            return () => clearTimeout(timer);
        }
    }, [form, validateForm]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
    
        if (files) {
            const fileArray = Array.from(files);
            setForm(prev => ({
                ...prev,
                [name]: name === "media" ? [...prev.media, ...fileArray.map(file => ({ file, type: file.type.startsWith("image/") ? "image" : "video" }))] : [...prev.additionalDocuments, ...fileArray],
            }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleFileUpload = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;
        setForm(prev => ({ ...prev, [type]: file }));
    };

    const handleRemoveFile = (type) => {
        setForm(prev => ({ ...prev, [type]: null }));
    };

    const handleAdditionalDocs = (e) => {
        const files = Array.from(e.target.files);
        setForm(prev => ({ ...prev, additionalDocuments: [...prev.additionalDocuments, ...files] }));
    };

    const handleRemoveAdditionalDoc = (index) => {
        setForm(prev => ({ ...prev, additionalDocuments: prev.additionalDocuments.filter((_, i) => i !== index) }));
    };

    const handleMediaUpload = (e) => {
        const files = Array.from(e.target.files);
        const newMedia = [...form.media];
        let videoCount = newMedia.filter(m => m.type === "video").length;
        let errorMsg = "";
        files.forEach(file => {
            const isImage = file.type.startsWith("image/");
            const isVideo = file.type === "video/mp4" || file.type === "video/webm";
            if (!isImage && !isVideo) {
                errorMsg = "Only images and .mp4/.webm videos are allowed.";
                return;
            }
            if (isVideo) {
                if (file.size > 30 * 1024 * 1024) {
                    errorMsg = "Each video must be less than 30MB.";
                    return;
                }
                if (!project && videoCount >= 2) {
                    errorMsg = "You can only upload up to 2 videos for new opportunities.";
                    return;
                }
                videoCount++;
            }
            newMedia.push({ file, type: isImage ? "image" : "video" });
        });
        if (errorMsg) {
            setError(errorMsg);
            return;
        }
        setForm(prev => ({ ...prev, media: newMedia }));
    };

    const handleRemoveMedia = (index) => {
        setForm(prev => ({ ...prev, media: prev.media.filter((_, i) => i !== index) }));
    };

    const handleNext = () => {
        if (activeStep === steps.length - 1) {
            handleSubmit();
        } else {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleSubmit = async () => {
        setError("");
        setSuccess("");
        setLoading(true);

        try {
            // Extract file names and metadata instead of File objects
            const imageUrls = form.media.filter(m => m.type === "image").map(m => ({
                name: m.file.name,
                size: m.file.size,
                type: m.file.type,
                // For now, we'll store file metadata. In production, you'd upload to Firebase Storage first
                url: null, // This would be the uploaded URL
            }));

            const documentUrls = form.additionalDocuments.map(file => ({
                name: file.name,
                size: file.size,
                type: file.type,
                // For now, we'll store file metadata. In production, you'd upload to Firebase Storage first
                url: null, // This would be the uploaded URL
            }));

            const projectData = {
                title: form.title,
                description: form.description,
                propertyType: form.propertyType,
                location: form.location,
                amount: parseFloat(form.totalBudget),
                expectedReturn: parseFloat(form.expectedReturn),
                duration: parseInt(form.executionTime),
                minimumInvestment: parseFloat(form.minimumInvestment),
                riskLevel: form.riskLevel,
                media: imageUrls, // Store metadata instead of File objects
                documents: documentUrls, // Store metadata instead of File objects
                agenteId: currentUser?.uid,
                status: "pending_approval",
                createdAt: new Date().toISOString(),
                // Add other fields as needed
            };
      
            await opportunitiesService.createOpportunity(projectData);
            setSuccess(t("opportunity.submittedForApproval", "Oportunidad enviada para aprobación"));
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(err.message || t("opportunity.creationError", "Error al crear la oportunidad"));
        } finally {
            setLoading(false);
        }
    };

    const canProceed = () => {
        switch (activeStep) {
        case 0: return form.title && form.description && form.propertyType;
        case 1: return form.totalBudget && form.expectedReturn && form.executionTime;
        case 2: return (
            form.media.filter(m => m.type === "image").length >= 2 &&
        form.profitabilityAnalysis &&
        form.notaSimple &&
        form.projectBrochure
        );
        default: return true;
        }
    };

    const getStepContent = (step) => {
    // Move videoCount declaration here for use in case 2
        // eslint-disable-next-line no-unused-vars
        const videoCount = form.media.filter(m => m.type === "video").length;
        switch (step) {
        case 0:
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            label={t("opportunity.title", "Título del Proyecto")}
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            fullWidth
                            required
                            helperText={`${form.title.length}/10 min characters`}
                            error={form.title.length > 0 && form.title.length < 10}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t("opportunity.description", "Descripción del Proyecto")}
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={4}
                            required
                            helperText={`${form.description.length}/50 min characters`}
                            error={form.description.length > 0 && form.description.length < 50}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                            <InputLabel>{t("opportunity.propertyType", "Tipo de Propiedad")}</InputLabel>
                            <Select name="propertyType" value={form.propertyType} onChange={handleChange} label={t("opportunity.propertyType", "Tipo de Propiedad")}>
                                {Object.entries(PROPERTY_TYPES).map(([key, value]) => (
                                    <MenuItem key={value} value={value}>
                                        {key.replace("_", " ").toUpperCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={t("opportunity.location", "Ubicación")}
                            name="location"
                            value={form.location}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                    </Grid>
                </Grid>
            );

        case 1:
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={t("opportunity.totalBudget", "Presupuesto Total (€)")}
                            name="totalBudget"
                            type="number"
                            value={form.totalBudget}
                            onChange={handleChange}
                            fullWidth
                            required
                            helperText={t("opportunity.minBudget", "Mínimo €100,000")}
                            error={!!(form.totalBudget && parseFloat(form.totalBudget) < 100000)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={t("opportunity.expectedReturn", "Rentabilidad Esperada (%)")}
                            name="expectedReturn"
                            type="number"
                            value={form.expectedReturn}
                            onChange={handleChange}
                            fullWidth
                            required
                            helperText={t("opportunity.returnRange", "Entre 5% y 50%")}
                            error={!!(form.expectedReturn && (parseFloat(form.expectedReturn) < 5 || parseFloat(form.expectedReturn) > 50))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={t("opportunity.executionTime", "Plazo de Ejecución (meses)")}
                            name="executionTime"
                            type="number"
                            value={form.executionTime}
                            onChange={handleChange}
                            fullWidth
                            required
                            helperText={t("opportunity.executionRange", "Entre 6 y 120 meses")}
                            error={!!(form.executionTime && (parseInt(form.executionTime) < 6 || parseInt(form.executionTime) > 120))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={t("opportunity.minimumInvestment", "Inversión Mínima (€)")}
                            name="minimumInvestment"
                            type="number"
                            value={form.minimumInvestment}
                            onChange={handleChange}
                            fullWidth
                            required
                        />
                    </Grid>
                </Grid>
            );

        case 2:
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>Upload Media (Images & Videos)</Typography>
                        <Button variant="outlined" component="label" startIcon={<Upload />} sx={{ mb: 2 }}>
                Upload Media
                            <input type="file" multiple accept="image/*,video/mp4,video/webm" hidden onChange={handleMediaUpload} />
                        </Button>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {form.media.map((media, index) => (
                                media.type === "image" ? (
                                    <Box key={index} sx={{ position: "relative" }}>
                                        <img
                                            src={URL.createObjectURL(media.file)}
                                            alt={media.file.name}
                                            style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }}
                                        />
                                        <IconButton size="small" onClick={() => handleRemoveMedia(index)} sx={{ position: "absolute", top: 0, right: 0 }}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Box key={index} sx={{ position: "relative" }}>
                                        <video
                                            width={80}
                                            height={80}
                                            style={{ borderRadius: 4, border: "1px solid #ccc", objectFit: "cover" }}
                                            controls
                                            src={URL.createObjectURL(media.file)}
                                        />
                                        <IconButton size="small" onClick={() => handleRemoveMedia(index)} sx={{ position: "absolute", top: 0, right: 0 }}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )
                            ))}
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 1 }}>
                You can upload images and up to 2 videos (.mp4, .webm, max 30MB each for new opportunities).
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>{t("opportunity.legalDocumentation", "Documentación Legal")}</Typography>
                        {/* Profitability Analysis */}
                        <Box sx={{ mb: 2 }}>
                            <Button variant="outlined" component="label" startIcon={<Upload />}>
                  Subir Análisis de Rentabilidad
                                <input type="file" accept=".xlsx,.xls,.csv,.ods,.xlsm" hidden onChange={e => handleFileUpload(e, "profitabilityAnalysis")} />
                            </Button>
                            {form.profitabilityAnalysis && (
                                <Chip
                                    label={form.profitabilityAnalysis.name}
                                    onDelete={() => handleRemoveFile("profitabilityAnalysis")}
                                    deleteIcon={<Delete />}
                                    color="success"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Box>
                        {/* Nota Simple */}
                        <Box sx={{ mb: 2 }}>
                            <Button variant="outlined" component="label" startIcon={<Upload />}>
                  Subir Nota Simple / Property Registry Extract
                                <input type="file" accept=".pdf,.doc,.docx,.txt,.rtf,.odt" hidden onChange={e => handleFileUpload(e, "notaSimple")} />
                            </Button>
                            {form.notaSimple && (
                                <Chip
                                    label={form.notaSimple.name}
                                    onDelete={() => handleRemoveFile("notaSimple")}
                                    deleteIcon={<Delete />}
                                    color="success"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Box>
                        {/* Project Brochure */}
                        <Box sx={{ mb: 2 }}>
                            <Button variant="outlined" component="label" startIcon={<Upload />}>
                  Subir Dossier del Proyecto / Brochure
                                <input type="file" accept=".pdf,.doc,.docx,.txt,.rtf,.odt" hidden onChange={e => handleFileUpload(e, "projectBrochure")} />
                            </Button>
                            {form.projectBrochure && (
                                <Chip
                                    label={form.projectBrochure.name}
                                    onDelete={() => handleRemoveFile("projectBrochure")}
                                    deleteIcon={<Delete />}
                                    color="success"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Box>
                        {/* Additional Documents (optional) */}
                        <Box sx={{ mb: 2 }}>
                            <Button variant="outlined" component="label" startIcon={<Upload />}>
                  Subir Documentos Adicionales (opcional)
                                <input type="file" multiple hidden onChange={handleAdditionalDocs} />
                            </Button>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                {form.additionalDocuments.map((file, index) => (
                                    <Chip
                                        key={index}
                                        label={file.name}
                                        onDelete={() => handleRemoveAdditionalDoc(index)}
                                        deleteIcon={<Delete />}
                                        color="default"
                                    />
                                ))}
                            </Box>
                        </Box>
                        {/* Visual Checklist for Required Documents */}
                        <Box sx={{ mt: 3, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Checklist de Documentos Requeridos:</Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {form.profitabilityAnalysis ? <CheckCircle color="success" /> : <Error color="warning" />}
                                    <Typography color={form.profitabilityAnalysis ? "success.main" : "warning.main"}>
                      Análisis de Rentabilidad (.xlsx, .xls, .csv, .ods, .xlsm)
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {form.notaSimple ? <CheckCircle color="success" /> : <Error color="warning" />}
                                    <Typography color={form.notaSimple ? "success.main" : "warning.main"}>
                      Nota Simple / Property Registry Extract (.pdf, .doc, .docx, .txt, .rtf, .odt)
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {form.projectBrochure ? <CheckCircle color="success" /> : <Error color="warning" />}
                                    <Typography color={form.projectBrochure ? "success.main" : "warning.main"}>
                      Dossier del Proyecto / Brochure (.pdf, .doc, .docx, .txt, .rtf, .odt)
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            );

        case 3:
            return (
                <Box>
                    <Typography variant="h6" gutterBottom>Resumen del Proyecto</Typography>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6">{form.title}</Typography>
                            <Typography variant="body2" color="textSecondary">{form.description}</Typography>
                            <Typography variant="body2">Presupuesto: €{form.totalBudget}</Typography>
                            <Typography variant="body2">Rentabilidad: {form.expectedReturn}%</Typography>
                        </CardContent>
                    </Card>

                    {validation && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Validación del Proyecto</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                    <Typography>Puntuación:</Typography>
                                    <Chip label={`${validation.score || 0}/100`} color={validation.isValid ? "success" : "warning"} />
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={validation.score || 0}
                                    color={validation.isValid ? "success" : "warning"}
                                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                                />
                  
                                {validation.issues && validation.issues.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="error" gutterBottom>Problemas encontrados:</Typography>
                                        {validation.issues.map((issue, index) => (
                                            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                <Error color="error" fontSize="small" />
                                                <Typography variant="body2">{issue}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {validation.recommendations && validation.recommendations.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>Recomendaciones:</Typography>
                                        {validation.recommendations.map((rec, index) => (
                                            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                <Info color="primary" fontSize="small" />
                                                <Typography variant="body2">{rec}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Box>
            );

        default:
            return null;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Assignment color="primary" />
                    {project ? t("opportunity.editProject", "Editar Proyecto") : t("opportunity.createNewProject", "Crear Nuevo Proyecto")}
                </Box>
            </DialogTitle>
      
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {getStepContent(activeStep)}

                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} disabled={loading}>{t("common.cancel", "Cancelar")}</Button>
                {activeStep > 0 && <Button onClick={handleBack} disabled={loading}>{t("common.back", "Atrás")}</Button>}
                <Button
                    onClick={handleNext}
                    variant="contained"
                    disabled={loading || !canProceed()}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? t("common.processing", "Procesando...") : 
                        activeStep === steps.length - 1 ? 
                            (project ? t("common.update", "Actualizar") : t("opportunity.createProject", "Crear Proyecto")) : 
                            t("common.next", "Siguiente")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OptimizedAgentOpportunityForm; 